"use client";

import { HugeiconsIcon } from "@hugeicons/react";
import { Download01Icon, FileSpreadsheetIcon, Pdf01Icon } from "@hugeicons-pro/core-bulk-rounded";

import { Menu } from "@base-ui/react/menu";

import type { Column } from "@/components/dashboard/breakdown-table";
import { downloadCsv, downloadXls, printTable, type Cell } from "@/lib/export";

interface ExportMenuProps<T> {
  /** Base file name and Excel sheet / PDF title. */
  name: string;
  columns: Column<T>[];
  rows: T[];
}

/** CSV / Excel / PDF export control shown in a table header. Builds the export
 *  grid from each column's `exportValue` (columns without one export blank). */
export function ExportMenu<T>({ name, columns, rows }: ExportMenuProps<T>) {
  const build = () => {
    const cols = columns.filter((c) => c.exportValue);
    const headers = cols.map((c) => c.header);
    const data: Cell[][] = rows.map((row) => cols.map((c) => c.exportValue!(row)));
    return { headers, data };
  };
  const file = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const items = [
    { key: "csv", label: "CSV", Icon: FileSpreadsheetIcon, run: () => { const { headers, data } = build(); downloadCsv(file, headers, data); } },
    { key: "xls", label: "Excel (.xls)", Icon: FileSpreadsheetIcon, run: () => { const { headers, data } = build(); downloadXls(file, name, headers, data); } },
    { key: "pdf", label: "PDF (print)", Icon: Pdf01Icon, run: () => { const { headers, data } = build(); printTable(name, headers, data); } },
  ];

  return (
    <Menu.Root>
      <Menu.Trigger className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 text-xs font-medium text-muted-foreground outline-none ring-ring/24 transition-colors hover:text-foreground focus-visible:ring-[3px] data-[popup-open]:bg-accent data-[popup-open]:text-foreground">
        <HugeiconsIcon icon={Download01Icon} size={14} />
        Exporteer
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner className="z-50 outline-none" side="bottom" align="end" sideOffset={4}>
          <Menu.Popup className="min-w-40 origin-(--transform-origin) rounded-lg border bg-popover p-1 text-foreground shadow-lg/5 outline-none">
            {items.map((it) => (
              <Menu.Item
                key={it.key}
                onClick={it.run}
                className="flex min-h-8 cursor-default items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-sm outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
              >
                <HugeiconsIcon icon={it.Icon} size={16} className="text-muted-foreground" />
                {it.label}
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
